import { useState, useContext } from 'react';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import { Mail } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../sporttia/all';
import { useAPI } from '../../lib/hooks';
import AuthScreen, {
    AuthBrandHeader,
    authButtonSx,
    authFieldSx,
} from './AuthScreen';

type ApiError = {
    error: {
        msg: string;
    };
};

/**
 * Recuperar contraseña. Comparte carcasa con la pantalla de entrar —a un clic
 * de distancia— para que el enlace no lleve a un sitio que parece otra
 * aplicación.
 */
export default function RecoverPassword() {
    // Con encadenamiento opcional a propósito: si el entorno no trae la
    // configuración del captcha, desestructurar directamente reventaba la
    // pantalla entera y el usuario se quedaba sin ver ni el enlace de vuelta.
    // Sin clave no se pinta el captcha, y sin captcha el botón sigue
    // bloqueado: la comprobación no se relaja, solo deja de tirar la página.
    const recaptchaSiteKey = (window.runtimeConfig as any)?.reCaptcha?.siteKey;

    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { migrationApis } = cxt;
    const [request] = useAPI(migrationApis);
    const [email, setEmail] = useState<string>('');
    const [captchaToken, setCaptchaValue] = useState<string | null>();
    const [captchaAction, setCaptchaAction] = useState<string>();
    const [sending, setSending] = useState<boolean>(false);

    // Sin correo o sin captcha no hay nada que enviar, y el botón lo dice
    // antes de que lo pulsen en vez de tragarse el clic en silencio.
    const canSend = Boolean(email && captchaToken && captchaAction);

    function send() {
        if (!canSend || sending) {
            return;
        }

        setSending(true);

        request('PUT', `/recover`, { email, captchaToken, captchaAction })
            .then(() => {
                cxt.showMessage('S', t('view.recoverPassword.sendEmail'));
            })
            .catch((response: ApiError) => {
                cxt.showMessage('E', response.error.msg);
            })
            .finally(() => {
                setSending(false);
            });
    }

    return (
        <AuthScreen>
            <AuthBrandHeader />

            <Box component="header" className="mb-8 text-center">
                <h1 className="text-3xl font-semibold sm:text-4xl">
                    {t('view.recoverPassword.title')}
                </h1>
                <p className="text-auth-muted mt-2 text-base">
                    {t('view.recoverPassword.instructions')}
                </p>
            </Box>

            <Box className="flex flex-col gap-4">
                <TextField
                    autoFocus
                    fullWidth
                    hiddenLabel
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder={t('view.recoverPassword.writeYourEmail')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={authFieldSx}
                    slotProps={{
                        htmlInput: {
                            'aria-label': t(
                                'view.recoverPassword.writeYourEmail',
                            ),
                        },
                        input: {
                            startAdornment: (
                                <Mail
                                    size={20}
                                    className="text-auth-field-muted mr-3 shrink-0"
                                />
                            ),
                        },
                    }}
                />

                {/* El captcha en variante oscura: sobre la banda, la clara se
                    recorta como un parche blanco en mitad del formulario. */}
                {recaptchaSiteKey && (
                    <Box className="flex justify-center">
                        <ReCAPTCHA
                            sitekey={recaptchaSiteKey}
                            theme="dark"
                            badge="inline"
                            onChange={(token: string | null) => {
                                setCaptchaValue(token);
                                setCaptchaAction('password_reset');
                            }}
                        />
                    </Box>
                )}

                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    disabled={!canSend || sending}
                    onClick={send}
                    sx={authButtonSx}
                    startIcon={
                        sending ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : null
                    }
                    className="h-14 rounded-xl text-base font-semibold normal-case"
                >
                    {t('view.recoverPassword.sendLink')}
                </Button>

                {/* El botón desactivado no explica por sí solo qué falta, y
                    aquí faltan dos cosas distintas. */}
                {!canSend && (
                    <p className="text-auth-muted text-center text-sm">
                        {t('view.recoverPassword.completeToSend')}
                    </p>
                )}

                <p className="pt-2 text-center">
                    <a
                        href="/login"
                        className="text-auth-foreground text-sm no-underline hover:underline"
                    >
                        {t('view.recoverPassword.backToLogin')}
                    </a>
                </p>
            </Box>
        </AuthScreen>
    );
}
